import React, { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";

const API_BASE = process.env.REACT_APP_API_URL || "";

const T = {
  rose: "#e11d48", roseDeep: "#be123c", roseSoft: "#fff1f2",
  violet: "#7c3aed", teal: "#0d9488", amber: "#d97706",
  text: "#1a1a1a", muted: "#6b7280", border: "#e5e7eb",
  card: "#ffffff", bg: "#f5f7fa",
};

// ── Helpers ────────────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem("soul_session_token");
}

async function apiCreateShareLink({ type, platform, config, label }) {
  const res = await fetch(`${API_BASE}/api/share/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ type, platform, config, label }),
  });
  if (!res.ok) throw new Error("Failed to create share link");
  return res.json();
}

export async function apiFetchShareLinks() {
  const res = await fetch(`${API_BASE}/api/user/share-links`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error("Failed to fetch share links");
  return res.json();
}

export async function apiToggleShareLink(id, is_active) {
  const res = await fetch(`${API_BASE}/api/share/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ is_active }),
  });
  if (!res.ok) throw new Error("Failed to update");
  return res.json();
}

export async function apiDeleteShareLink(id) {
  const res = await fetch(`${API_BASE}/api/share/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error("Failed to delete");
  return res.json();
}

export async function apiUpdateShareLinkLabel(id, label) {
  const res = await fetch(`${API_BASE}/api/share/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ label }),
  });
  if (!res.ok) throw new Error("Failed to update label");
  return res.json();
}

// ── Social share URL builders ──────────────────────────────────────
function buildSocialUrl(platform, shareUrl, text) {
  const enc = encodeURIComponent;
  switch (platform) {
    case "twitter":  return `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`;
    case "whatsapp": return `https://api.whatsapp.com/send?text=${enc(text + " " + shareUrl)}`;
    case "facebook": return `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`;
    case "threads":  return `https://www.threads.net/intent/post?text=${enc(text + " " + shareUrl)}`;
    case "telegram": return `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(text)}`;
    default: return null;
  }
}

const PLATFORM_ICONS = {
  qr:       { label: "QR Code",   bg: "#1a1a1a", emoji: "⬛" },
  link:     { label: "Copy Link", bg: T.teal,     emoji: "🔗" },
  twitter:  { label: "X / Twitter", bg: "#000",   emoji: "𝕏" },
  whatsapp: { label: "WhatsApp", bg: "#25D366",   emoji: "💬" },
  facebook: { label: "Facebook", bg: "#1877F2",   emoji: "f" },
  threads:  { label: "Threads",  bg: "#101010",   emoji: "🅣" },
  telegram: { label: "Telegram", bg: "#229ED9",   emoji: "✈️" },
};

// ── QR Code Modal ──────────────────────────────────────────────────
export function QRModal({ url, onClose, title }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: 16, padding: 32, maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 14, fontWeight: 700, color: T.rose, letterSpacing: "0.08em", marginBottom: 8 }}>SCAN TO JOIN</div>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 20 }}>{title}</div>
        <div style={{ display: "inline-block", padding: 12, background: "#fff", borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 20 }}>
          <QRCodeSVG
            value={url}
            size={200}
            level="H"
            imageSettings={{
              src: "/favicon.ico",
              height: 36,
              width: 36,
              excavate: true,
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 20, wordBreak: "break-all" }}>{url}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => { navigator.clipboard.writeText(url); }}
            style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: T.roseSoft || "#fff1f2", border: `1px solid ${T.rose}40`, color: T.rose, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
          >Copy Link</button>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, color: T.muted, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Share Method Grid ──────────────────────────────────────────────
function ShareMethodGrid({ onSelect, type }) {
  const platforms = ["qr", "link", "twitter", "whatsapp", "facebook", "threads", "telegram"];
  const compatText = "Take a free compatibility test with me on settup!";
  const snapText = "Check out my personality snapshot on settup!";
  const shareText = type === "compatibility_test" ? compatText : snapText;

  return (
    <div style={{ padding: "16px 0 8px" }}>
      <div style={{ fontSize: 12, color: T.muted, fontWeight: 600, letterSpacing: "0.08em", marginBottom: 14, textAlign: "center" }}>
        SHARE VIA
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {platforms.map((p, i) => {
          const info = PLATFORM_ICONS[p];
          return (
            <button
              key={p}
              onClick={() => onSelect(p, shareText)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                padding: "12px 8px", borderRadius: 12, border: `1px solid ${T.border}`,
                background: "#fff", cursor: "pointer", fontFamily: "inherit",
                animation: `emojiPop 0.35s ease ${i * 0.04}s both`,
                transition: "all 0.18s",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{
                width: 38, height: 38, borderRadius: "50%", background: info.bg,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: p === "twitter" ? 15 : p === "threads" ? 14 : 18, color: "#fff", fontWeight: 900,
              }}>{info.emoji}</div>
              <span style={{ fontSize: 10, color: T.muted, fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>{info.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Share Flyout (dropdown on "Share with friends") ────────────────
export function ShareFlyout({ userAnswers, userContext, STAGES, SNAPSHOT_SECTIONS, getStageProgress }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // "menu" | "compat_grid" | "snap_sheet" | "snap_grid" | "generating" | "done"
  const [selectedType, setSelectedType] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const resetToMenu = () => {
    setView("menu");
    setSelectedType(null);
    setShareLink(null);
    setError(null);
    setCopied(false);
  };

  const handleSelectMethod = useCallback(async (platform, shareText, type, config) => {
    setView("generating");
    setError(null);
    try {
      const label = `${PLATFORM_ICONS[platform]?.label || platform} — ${new Date().toLocaleDateString()}`;
      const link = await apiCreateShareLink({ type, platform, config: config || {}, label });
      setShareLink(link);

      if (platform === "qr") {
        setView("done");
        setQrOpen(true);
      } else if (platform === "link") {
        navigator.clipboard.writeText(link.url).catch(() => {});
        setCopied(true);
        setView("done");
      } else {
        const socialUrl = buildSocialUrl(platform, link.url, shareText);
        if (socialUrl) window.open(socialUrl, "_blank", "noopener,noreferrer");
        setView("done");
      }
    } catch (err) {
      setError("Couldn't create link — check your connection.");
      setView("compat_grid"); // or stay on grid
    }
  }, []);

  // Snapshot sheet state
  const [snapSheetOpen, setSnapSheetOpen] = useState(false);
  const [snapPlatform, setSnapPlatform] = useState(null);
  const [snapText, setSnapText] = useState("");

  const handleSnapMethodSelect = (platform, shareText) => {
    setSnapPlatform(platform);
    setSnapText(shareText);
    setSnapSheetOpen(true);
    setOpen(false);
  };

  const handleSnapConfirm = async (config) => {
    setSnapSheetOpen(false);
    setOpen(true);
    await handleSelectMethod(snapPlatform, snapText, "personality_snapshot", config);
  };

  return (
    <>
      <div ref={ref} style={{ position: "relative", display: "inline-flex" }}>
        {/* Trigger */}
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.muted, cursor: "pointer", padding: "4px 8px", borderRadius: 6, transition: "all 0.15s" }}
          onClick={() => { setOpen(o => !o); resetToMenu(); }}
          onMouseEnter={e => { e.currentTarget.style.color = T.rose; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.muted; }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share with friends
        </div>

        {/* Dropdown Panel */}
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 999,
            background: "#fff", borderRadius: 14, border: `1px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(0,0,0,0.14)", minWidth: 300, maxWidth: 340,
            animation: "reactionSlideUp 0.2s ease both", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Share with friends</div>
              {view !== "menu" && (
                <button onClick={resetToMenu} style={{ fontSize: 11, color: T.rose, background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 2, fontFamily: "inherit" }}>← Back</button>
              )}
            </div>

            <div style={{ padding: "12px 16px 16px" }}>
              {/* Main menu */}
              {view === "menu" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <MenuOption
                    icon="⚡"
                    title="Test Compatibility"
                    desc="Share a link — friends sign up and see how compatible they are with you"
                    onClick={() => setView("compat_grid")}
                  />
                  <MenuOption
                    icon="🧬"
                    title="Share Personality Snapshot"
                    desc="Choose which stages of your personality to share publicly"
                    onClick={() => setView("snap_grid")}
                  />
                </div>
              )}

              {/* Compatibility test: pick platform */}
              {view === "compat_grid" && (
                <ShareMethodGrid
                  type="compatibility_test"
                  onSelect={(platform, text) => handleSelectMethod(platform, text, "compatibility_test", {})}
                />
              )}

              {/* Snapshot: pick platform first, then configure stages in sheet */}
              {view === "snap_grid" && (
                <ShareMethodGrid
                  type="personality_snapshot"
                  onSelect={handleSnapMethodSelect}
                />
              )}

              {/* Generating */}
              {view === "generating" && (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={{ fontSize: 28, marginBottom: 12, animation: "orbPulse 1s ease-in-out infinite alternate" }}>🔗</div>
                  <div style={{ fontSize: 13, color: T.muted }}>Creating your share link…</div>
                </div>
              )}

              {/* Done */}
              {view === "done" && shareLink && (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>
                    {copied ? "Link copied!" : "Shared!"}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted, marginBottom: 14, wordBreak: "break-all" }}>
                    {shareLink.url}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => { navigator.clipboard.writeText(shareLink.url); setCopied(true); }}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: "#fff1f2", border: `1px solid ${T.rose}40`, color: T.rose, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                    >📋 Copy</button>
                    <button
                      onClick={() => setQrOpen(true)}
                      style={{ flex: 1, padding: "8px 0", borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontWeight: 600, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}
                    >⬛ QR Code</button>
                  </div>
                  <button onClick={resetToMenu} style={{ marginTop: 10, width: "100%", padding: "8px 0", borderRadius: 8, background: "none", border: `1px solid ${T.border}`, color: T.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Share another</button>
                </div>
              )}

              {error && (
                <div style={{ fontSize: 12, color: "#ef4444", textAlign: "center", marginTop: 8 }}>{error}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {qrOpen && shareLink && (
        <QRModal
          url={shareLink.url}
          title={selectedType === "compatibility_test" ? "Take a Compatibility Test" : "My Personality Snapshot"}
          onClose={() => setQrOpen(false)}
        />
      )}

      {/* Snapshot Sheet */}
      {snapSheetOpen && (
        <SnapshotShareSheet
          STAGES={STAGES}
          SNAPSHOT_SECTIONS={SNAPSHOT_SECTIONS}
          getStageProgress={getStageProgress}
          userContext={userContext}
          platform={snapPlatform}
          onConfirm={handleSnapConfirm}
          onClose={() => setSnapSheetOpen(false)}
        />
      )}
    </>
  );
}

// ── Menu Option row ────────────────────────────────────────────────
function MenuOption({ icon, title, desc, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 14px",
        borderRadius: 10, border: `1px solid ${T.border}`, cursor: "pointer",
        transition: "all 0.18s", background: "#fff",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = T.rose; e.currentTarget.style.background = "#fff8f8"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "#fff"; }}
    >
      <div style={{ fontSize: 22, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>{desc}</div>
      </div>
      <div style={{ marginLeft: "auto", fontSize: 16, color: T.muted, alignSelf: "center", flexShrink: 0 }}>›</div>
    </div>
  );
}

// ── Snapshot Share Sheet ───────────────────────────────────────────
function SnapshotShareSheet({ STAGES, SNAPSHOT_SECTIONS, getStageProgress, userContext, platform, onConfirm, onClose }) {
  const completedSections = SNAPSHOT_SECTIONS
    ? SNAPSHOT_SECTIONS.filter(sec => getStageProgress && getStageProgress(sec.stage)?.done)
    : [];

  // Build default config: all stages & context shared = true
  const [stageConfig, setStageConfig] = useState(() => {
    const cfg = {};
    (completedSections).forEach(sec => {
      cfg[sec.stage] = { share: true, context: true };
    });
    return cfg;
  });
  const [expanded, setExpanded] = useState({});

  const toggleStageShare = (stage) => {
    setStageConfig(prev => ({ ...prev, [stage]: { ...prev[stage], share: !prev[stage].share } }));
  };
  const toggleContextShare = (stage) => {
    setStageConfig(prev => ({ ...prev, [stage]: { ...prev[stage], context: !prev[stage].context } }));
  };
  const toggleExpand = (stage) => {
    setExpanded(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  const sharedCount = Object.values(stageConfig).filter(v => v.share).length;
  const platformLabel = PLATFORM_ICONS[platform]?.label || platform;

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 560,
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
          animation: "sheetSlideUp 0.32s cubic-bezier(0.32,0.72,0,1) both",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Sheet handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: 12, paddingBottom: 4, flexShrink: 0 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: T.border }} />
        </div>

        {/* Sheet header */}
        <div style={{ padding: "12px 20px 14px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 4 }}>Choose What to Share</div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {sharedCount} of {completedSections.length} stages will be shared via {platformLabel}
          </div>
        </div>

        {/* Stage accordion list */}
        <div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
          {completedSections.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: T.muted, fontSize: 14 }}>
              Complete some interview stages first to share your snapshot.
            </div>
          )}
          {completedSections.map((sec) => {
            const cfg = stageConfig[sec.stage] || { share: true, context: true };
            const stageCtx = (userContext && userContext[sec.stage]) || [];
            const isExpanded = expanded[sec.stage];
            const insight = sec.getInsight ? sec.getInsight() : null;
            const stageName = STAGES?.[sec.stage]?.name || sec.title;
            const stageDesc = STAGES?.[sec.stage]?.desc || "";

            return (
              <div
                key={sec.stage}
                style={{
                  borderBottom: `1px solid ${T.border}`,
                  opacity: cfg.share ? 1 : 0.45,
                  transition: "opacity 0.2s",
                }}
              >
                {/* Stage row */}
                <div style={{ display: "flex", alignItems: "center", padding: "13px 20px", gap: 10 }}>
                  {/* Expand toggle */}
                  <button
                    onClick={() => toggleExpand(sec.stage)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: T.muted, padding: "0 4px", flexShrink: 0, fontFamily: "inherit" }}
                  >
                    {isExpanded ? "▼" : "▶"}
                  </button>

                  {/* Stage name */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: cfg.share ? T.text : T.muted }}>{stageName}</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{stageDesc}</div>
                  </div>

                  {/* Share toggle pill */}
                  <ShareToggle
                    on={cfg.share}
                    onToggle={() => toggleStageShare(sec.stage)}
                  />
                </div>

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ paddingLeft: 44, paddingRight: 20, paddingBottom: 14 }}>
                    {/* Insight preview */}
                    {insight?.content && (
                      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, marginBottom: 10, padding: "8px 12px", background: T.bg, borderRadius: 8 }}>
                        {insight.content.length > 140 ? insight.content.slice(0, 140) + "…" : insight.content}
                      </div>
                    )}

                    {/* Context sub-section */}
                    {stageCtx.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.06em" }}>ADDED CONTEXT</div>
                          <ShareToggle
                            on={cfg.context}
                            onToggle={() => toggleContextShare(sec.stage)}
                            small
                          />
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {stageCtx.map((ctx, i) => (
                            <span key={i} style={{
                              fontSize: 11, padding: "3px 10px", borderRadius: 20,
                              background: cfg.context ? "#f0f4ff" : T.bg,
                              color: cfg.context ? "#4f46e5" : T.muted,
                              border: `1px solid ${cfg.context ? "#c7d2fe" : T.border}`,
                              transition: "all 0.2s",
                            }}>{ctx}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer CTA */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.border}`, flexShrink: 0, background: "#fff" }}>
          <button
            onClick={() => onConfirm(stageConfig)}
            disabled={sharedCount === 0}
            style={{
              width: "100%", padding: "14px 0", borderRadius: 10,
              background: sharedCount > 0 ? `linear-gradient(135deg, ${T.rose}, ${T.roseDeep})` : T.border,
              border: "none", color: "#fff", fontSize: 15, fontWeight: 800,
              cursor: sharedCount > 0 ? "pointer" : "not-allowed", fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >
            {sharedCount > 0 ? `Share ${sharedCount} Stage${sharedCount > 1 ? "s" : ""} via ${PLATFORM_ICONS[platform]?.label || platform}` : "Select at least one stage"}
          </button>
          <button onClick={onClose} style={{ width: "100%", marginTop: 8, padding: "10px 0", borderRadius: 10, background: "none", border: "none", color: T.muted, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Share Toggle pill ──────────────────────────────────────────────
function ShareToggle({ on, onToggle, small }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      style={{
        display: "flex", alignItems: "center", gap: 5, padding: small ? "3px 8px" : "4px 10px",
        borderRadius: 20, border: `1px solid ${on ? T.rose : T.border}`,
        background: on ? "#fff1f2" : T.bg, cursor: "pointer", fontFamily: "inherit",
        fontSize: small ? 10 : 11, fontWeight: 700, color: on ? T.rose : T.muted,
        transition: "all 0.18s", flexShrink: 0,
      }}
    >
      <div style={{
        width: small ? 8 : 10, height: small ? 8 : 10, borderRadius: "50%",
        background: on ? T.rose : T.border, transition: "background 0.18s",
      }} />
      {on ? "Share" : "Skip"}
    </button>
  );
}

// ── Share Links Settings Panel ─────────────────────────────────────
export function ShareLinksPanel() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [qrLink, setQrLink] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetchShareLinks();
      setLinks(data);
    } catch {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (link) => {
    try {
      await apiToggleShareLink(link.id, !link.is_active);
      setLinks(prev => prev.map(l => l.id === link.id ? { ...l, is_active: !l.is_active } : l));
    } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this share link? This cannot be undone.")) return;
    try {
      await apiDeleteShareLink(id);
      setLinks(prev => prev.filter(l => l.id !== id));
    } catch {}
  };

  const handleSaveLabel = async (id) => {
    try {
      await apiUpdateShareLinkLabel(id, editLabel);
      setLinks(prev => prev.map(l => l.id === id ? { ...l, label: editLabel } : l));
      setEditingId(null);
    } catch {}
  };

  if (loading) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center", color: T.muted, fontSize: 14 }}>
        Loading share links…
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, marginBottom: 16 }}>
        Track and manage your shared compatibility tests and personality snapshots. Toggle to disable or delete to revoke access.
      </div>

      {links.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: T.muted, fontSize: 14, border: `1px dashed ${T.border}`, borderRadius: 10 }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔗</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>No share links yet</div>
          <div style={{ fontSize: 12 }}>Use "Share with friends" on your personality snapshot to create one.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {links.map(link => {
            const typeLabel = link.type === "compatibility_test" ? "Compatibility Test" : "Personality Snapshot";
            const typeColor = link.type === "compatibility_test" ? T.violet : T.teal;
            const platformInfo = PLATFORM_ICONS[link.platform] || PLATFORM_ICONS.link;
            const createdDate = new Date(link.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
            const isEditing = editingId === link.id;

            return (
              <div
                key={link.id}
                style={{
                  background: link.is_active ? "#fff" : T.bg,
                  border: `1px solid ${link.is_active ? T.border : "#e5e7eb"}`,
                  borderRadius: 12, padding: "14px 16px",
                  opacity: link.is_active ? 1 : 0.65,
                  transition: "all 0.2s",
                }}
              >
                {/* Top row */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                  {/* Platform icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", background: platformInfo.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: link.platform === "twitter" ? 13 : 16, color: "#fff", fontWeight: 900, flexShrink: 0,
                  }}>{platformInfo.emoji}</div>

                  <div style={{ flex: 1 }}>
                    {/* Type badge + label */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: typeColor + "18", color: typeColor, letterSpacing: "0.04em" }}>
                        {typeLabel.toUpperCase()}
                      </span>
                      {!link.is_active && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#f3f4f6", color: T.muted }}>INACTIVE</span>
                      )}
                    </div>

                    {/* Editable label */}
                    {isEditing ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          value={editLabel}
                          onChange={e => setEditLabel(e.target.value)}
                          style={{ flex: 1, fontSize: 12, padding: "4px 8px", border: `1px solid ${T.rose}`, borderRadius: 6, fontFamily: "inherit", outline: "none" }}
                          onKeyDown={e => { if (e.key === "Enter") handleSaveLabel(link.id); if (e.key === "Escape") setEditingId(null); }}
                          autoFocus
                        />
                        <button onClick={() => handleSaveLabel(link.id)} style={{ fontSize: 11, color: T.rose, background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontFamily: "inherit" }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ fontSize: 11, color: T.muted, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
                          {link.label || `${platformInfo.label} share`}
                        </span>
                        <button onClick={() => { setEditingId(link.id); setEditLabel(link.label || ""); }} style={{ fontSize: 10, color: T.muted, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>✏️</button>
                      </div>
                    )}

                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Created {createdDate}</div>
                  </div>
                </div>

                {/* Analytics row */}
                <div style={{ display: "flex", gap: 16, marginBottom: 12, paddingLeft: 46 }}>
                  <Stat label="Views" value={link.views} />
                  <Stat label="Signups" value={link.conversions} />
                  {link.tests_taken !== undefined && <Stat label="Tests Taken" value={link.tests_taken} />}
                </div>

                {/* URL + actions */}
                <div style={{ paddingLeft: 46, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    onClick={() => navigator.clipboard.writeText(link.url)}
                    style={{ fontSize: 11, padding: "5px 10px", borderRadius: 7, background: T.bg, border: `1px solid ${T.border}`, color: T.muted, cursor: "pointer", fontFamily: "inherit" }}
                  >📋 Copy</button>
                  <button
                    onClick={() => setQrLink(link)}
                    style={{ fontSize: 11, padding: "5px 10px", borderRadius: 7, background: T.bg, border: `1px solid ${T.border}`, color: T.muted, cursor: "pointer", fontFamily: "inherit" }}
                  >⬛ QR</button>
                  <button
                    onClick={() => handleToggle(link)}
                    style={{ fontSize: 11, padding: "5px 10px", borderRadius: 7, background: link.is_active ? "#fff1f2" : T.bg, border: `1px solid ${link.is_active ? T.rose + "40" : T.border}`, color: link.is_active ? T.rose : T.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}
                  >
                    {link.is_active ? "Disable" : "Enable"}
                  </button>
                  <button
                    onClick={() => handleDelete(link.id)}
                    style={{ fontSize: 11, padding: "5px 10px", borderRadius: 7, background: T.bg, border: `1px solid ${T.border}`, color: "#ef4444", cursor: "pointer", fontFamily: "inherit" }}
                  >🗑 Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {qrLink && (
        <QRModal
          url={qrLink.url}
          title={qrLink.type === "compatibility_test" ? "Take a Compatibility Test" : "My Personality Snapshot"}
          onClose={() => setQrLink(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{value ?? 0}</div>
      <div style={{ fontSize: 10, color: T.muted, fontWeight: 600, letterSpacing: "0.05em" }}>{label.toUpperCase()}</div>
    </div>
  );
}

// ── Join flow helpers ──────────────────────────────────────────────
export function detectShareToken() {
  const params = new URLSearchParams(window.location.search);
  const compat = params.get("compat");
  const snap = params.get("snap");
  if (compat) return { token: compat, type: "compatibility_test" };
  if (snap) return { token: snap, type: "personality_snapshot" };
  return null;
}

export async function fetchShareLinkData(token) {
  const res = await fetch(`${API_BASE}/api/share/${token}`);
  if (!res.ok) throw new Error("Link not found or expired");
  return res.json();
}

export async function recordShareEvent(token, event_type, new_user_id = null) {
  try {
    await fetch(`${API_BASE}/api/share/${token}/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_type, new_user_id }),
    });
  } catch {}
}
